/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  createRydeColumnHelper,
  fixedColumnNamesAF,
  gridControlsSF,
  IColumnDef,
  IRideTableDto,
} from "@ryde/shared";

import {
  colHandlingRideMapObj,
  idEmptyColumnObjLiveMap,
  lastColLiveMapObj,
  useIdColumnLiveMapObj,
} from "../../cells";
import { getFixedColumnsNamesFromTableDef } from "./helpers";
import { useDynamicTableDefColumns } from "./use-table-def.dynamic-columns";

const columnHelper = createRydeColumnHelper<IRideTableDto>();

export interface IUseTableDefsRideMap {
  tableDefsRidesMap: IColumnDef<IRideTableDto, any>[];
  columnKeys: string[];
}

export const useTableDefsRidesMap = (): IUseTableDefsRideMap => {
  const idColumnLiveMapObj = useIdColumnLiveMapObj();
  const gridControls = useRecoilValue(gridControlsSF("rideMap"));
  const { dynamicColumns } = useDynamicTableDefColumns({
    genericColumnHelper: columnHelper,
    gridControls,
  });
  const setFixedColumnNames = useSetRecoilState(fixedColumnNamesAF("rideMap"));

  const tableDefsRidesMap: IColumnDef<IRideTableDto, any>[] = useMemo(() => {
    if (!dynamicColumns.length) {
      return [];
    }

    const handlingCol = columnHelper.display(colHandlingRideMapObj);

    return [
      columnHelper.display(idEmptyColumnObjLiveMap()),
      handlingCol,
      columnHelper.accessor((row: IRideTableDto) => row.id, idColumnLiveMapObj),
      ...dynamicColumns,
      columnHelper.display(lastColLiveMapObj()),
    ];
  }, [dynamicColumns, idColumnLiveMapObj]);
  const columnKeys = useMemo(
    () => tableDefsRidesMap.filter(x => x.id).map(x => x.id || ""),
    [tableDefsRidesMap],
  );

  const fixedColumnNames = useMemo(() => {
    return getFixedColumnsNamesFromTableDef(tableDefsRidesMap);
  }, [tableDefsRidesMap]);

  useEffect(() => {
    setFixedColumnNames(fixedColumnNames);
  }, [fixedColumnNames, setFixedColumnNames]);

  return { tableDefsRidesMap, columnKeys };
};
